import { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { AiMessage } from "@/zapi-frontend/shared/ui/chat";
import { PresentationChatMessage as TPresentationChatMessage } from "../hooks/usePresentationChat";
import { PresentationChatMessage } from "./PresentationChatMessage";
import { GenerationSteps } from "./GenerationSteps";

type TPresentationChatMessageListProps = {
  messages: TPresentationChatMessage[];
  loadingMessageId: string | null;
  onActionButton: (messageText: string, messageId: string) => void;
  isSlidesGenerating?: boolean;
  hasSlides?: boolean;
};

export function PresentationChatMessageList(props: TPresentationChatMessageListProps) {
  const { t } = useTranslation("projectHome");
  const { messages, loadingMessageId, onActionButton, isSlidesGenerating, hasSlides } = props;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesHash = useMemo(() => messages.map((msg) => msg.id).join("-"), [messages]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timeout);
  }, [messagesHash, loadingMessageId]);

  if (messages.length === 0 && !isSlidesGenerating && !hasSlides) {
    return (
      <div className="h-full flex items-center justify-center p-[30px]">
        <FigmaTypography variant="body-2" color="zinc400" className="text-center">
          {t("presentation.chatEmptyLine1")}
          <br />
          {t("presentation.chatEmptyLine2")}
        </FigmaTypography>
      </div>
    );
  }

  if (messages.length === 0 && !isSlidesGenerating && hasSlides) {
    return (
      <div className="h-full overflow-y-auto px-4 py-4">
        <AiMessage className="w-full">
          <div className="text-[14px] leading-[22px] text-innoveraZinc-900">
            {t("presentation.outlineReady")}
          </div>
        </AiMessage>
      </div>
    );
  }

  if (messages.length === 0 && isSlidesGenerating) {
    return (
      <div className="h-full overflow-y-auto px-4 py-4">
        <AiMessage className="w-full">
          <GenerationSteps />
        </AiMessage>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className="h-full overflow-y-auto px-4 py-4">
      {messages.map((message) => (
        <PresentationChatMessage
          key={message.id}
          message={message}
          isLoading={message.id === loadingMessageId}
          onActionButton={onActionButton}
        />
      ))}
    </div>
  );
}
